import React from 'react';
import { MultiSelect, Option } from '../../../../../../../components/ui/multi-select';
import { CollapsibleFilterBar } from './CollapsibleFilterBar';

// Pipe-specific filter constants
const PIPE_STATUS_TYPES = ['Available', 'Consumed'] as const;
const PIPE_PROJECT_TYPES = ['Production', 'PQT'] as const;

interface PipeFilters {
  status: string[];
  pipeNo: string[];
  pipeNRI: string[];
  pipeProjectStage: string[];
  heatNumber: string[];
  outerDiameter: string[];
  wallThickness: string[];
  projectName: string[];
}

interface PipeFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filters: PipeFilters;
  onFilterChange: (key: keyof PipeFilters) => (value: string[]) => void;
  uniquePipeNumbers: string[];
  uniquePipeNRIs: string[];
  uniquePipeProjectStages: string[];
  uniqueHeatNumbers: string[];
  uniqueOuterDiameters: string[];
  uniqueWallThicknesses: string[];
  uniqueProjectNames: string[];
  hasActiveFilters: boolean;
  filteredCount: number;
  onResetFilters: () => void;
}

export const PipeFilters: React.FC<PipeFiltersProps> = ({
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  uniquePipeNumbers,
  uniquePipeNRIs,
  uniquePipeProjectStages,
  uniqueHeatNumbers,
  uniqueOuterDiameters,
  uniqueWallThicknesses,
  uniqueProjectNames,
  hasActiveFilters,
  filteredCount,
  onResetFilters,
}) => {
  // Convert arrays to options for MultiSelect
  const statusOptions: Option[] = PIPE_STATUS_TYPES.map(status => ({
    value: status,
    label: status
  }));

  const pipeNoOptions: Option[] = uniquePipeNumbers.map(pipeNo => ({
    value: pipeNo,
    label: pipeNo
  }));

  const pipeNRIOptions: Option[] = uniquePipeNRIs.map(pipeNRI => ({
    value: pipeNRI,
    label: pipeNRI
  }));

  const pipeProjectStageOptions: Option[] = PIPE_PROJECT_TYPES.map(pipeProjectStage => ({
    value: pipeProjectStage,
    label: pipeProjectStage
  }));

  const heatNumberOptions: Option[] = uniqueHeatNumbers.map(heatNumber => ({
    value: heatNumber,
    label: heatNumber
  }));

  const outerDiameterOptions: Option[] = uniqueOuterDiameters.map((od) => ({
    value: od,
    label: od,
  }));

  const wallThicknessOptions: Option[] = uniqueWallThicknesses.map((wt) => ({
    value: wt,
    label: wt,
  }));

  const projectNameOptions: Option[] = uniqueProjectNames.map((name) => ({
    value: name,
    label: name,
  }));

  const activeFilterCount =
    filters.projectName.length +
    filters.pipeNRI.length +
    filters.pipeNo.length +
    filters.heatNumber.length +
    filters.outerDiameter.length +
    filters.wallThickness.length +
    filters.pipeProjectStage.length +
    filters.status.length;

  return (
    <CollapsibleFilterBar
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search materials by project, NRI, assembly number, or details..."
      activeFilterCount={activeFilterCount}
      filteredCount={filteredCount}
      filteredCountLabel="materials"
      onResetFilters={onResetFilters}
    >
      <MultiSelect
        options={projectNameOptions}
        selected={filters.projectName}
        onChange={onFilterChange('projectName')}
        placeholder="Project Name"
      />
      <MultiSelect
        options={pipeNRIOptions}
        selected={filters.pipeNRI}
        onChange={onFilterChange('pipeNRI')}
        placeholder="Pipe NRI"
      />
      <MultiSelect
        options={pipeNoOptions}
        selected={filters.pipeNo}
        onChange={onFilterChange('pipeNo')}
        placeholder="Pipe No"
      />
      <MultiSelect
        options={heatNumberOptions}
        selected={filters.heatNumber}
        onChange={onFilterChange('heatNumber')}
        placeholder="Heat Number"
      />
      <MultiSelect
        options={outerDiameterOptions}
        selected={filters.outerDiameter}
        onChange={onFilterChange('outerDiameter')}
        placeholder="Outer Diameter"
      />
      <MultiSelect
        options={wallThicknessOptions}
        selected={filters.wallThickness}
        onChange={onFilterChange('wallThickness')}
        placeholder="Wall Thickness"
      />
      <MultiSelect
        options={pipeProjectStageOptions}
        selected={filters.pipeProjectStage}
        onChange={onFilterChange('pipeProjectStage')}
        placeholder="Stage"
        isSearchable={false}
      />
      <MultiSelect
        options={statusOptions}
        selected={filters.status}
        onChange={onFilterChange('status')}
        placeholder="Status"
        isSearchable={false}
      />
    </CollapsibleFilterBar>
  );
};
